<template>
  <b-row class="OvertimeApproval">
    <b-col cols="12" lg="10" offset-lg="1">
      <Notification v-bind.sync="notification" />
      <Notification v-bind.sync="approvalNotification">
        <h5>
          <v-icon name="table" scale="1.5" />
          <span class="ml-1" v-html="approvalNotifText"></span>
          <!-- TODO: link to list of pending approvals -->
          <!-- <b-link
            :to="{ name: 'overtime-approval' }"
            class="float-right"
          >
            back to list
            <v-icon name="angle-double-right" />
          </b-link> -->
        </h5>

        <div class="px-3 pt-2">
          <b-button
            size="md"
            class="rounded-0 mr-1"
            variant="outline-danger"
            @click="openConfirm('reject')"
          >
            Reject
          </b-button>
          <b-button
            size="md"
            class="rounded-0"
            variant="success"
            @click="openConfirm('approve')"
          >
            Approve
          </b-button>
        </div>
      </Notification>

      <OvertimeCard
        ref="ot_card"
        :no-submit="true"
        :readonly="true"
        :employee="userEmployeeData"
        :others="otherData"
      />
    </b-col>

    <Modal
      v-bind.sync="confirmModalOptions"
      @okBtn="approveOrReject"
      @cancelBtn="resetAction"
    >
      <template slot="default">
        <span v-html="confirmModalOptions.messageHtml"></span>
        <div
          v-if="approvalAction === 'reject'"
          class="row mt-5"
        >
          <b-col>
            <label class="col-form-label font-weight-bold pt-0" for="reject_reason">Comments</label>
            <b-form-textarea
              class="rounded-0"
              name="reject_reason"
              v-model="comments"
              rows="4"
              no-resize
            />
          </b-col>
        </div>
      </template>
    </Modal>
  </b-row>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import OvertimeCard from '@/components/OvertimeCard';
import Notification from '@/components/Notification';
import Modal from '@/components/Modal';
import FormService from '@/services/FormService';
import overtimeStatuses from '@/mixins/overtimeStatuses';

const form = new FormService();

export default {
  name: 'OvertimeApproval',
  mixins: [overtimeStatuses],
  components: {
    Notification,
    Modal,
    OvertimeCard,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const { key } = vm.$route.params;
      const { id } = vm.userData;

      vm.loadingOvertimeData = true;
      form.checkIfApprover('overtime', id, key)
        .then(({ success, message, isApprover }) => {
          if (!success || !isApprover) {
            vm.setAlert({
              show: 5,
              variant: 'danger',
              fade: true,
              dismissible: true,
              message: typeof message === 'string' ? message : 'An error occurred',
              icon: {
                name: 'exclamation',
              },
            });
          }
          return success;
        })
        .catch(() => {
          vm.setAlert({
            show: 5,
            variant: 'danger',
            fade: true,
            dismissible: true,
            message: 'An error occurred',
            icon: {
              name: 'exclamation',
            },
          });

          return false;
        })
        .then((success) => {
          if (!success) {
            return vm.$router.push({ name: 'home' });
          }

          // User is the verified approver, get timesheet data
          vm.startLoadingRequest();
          vm.startLoadingPage();
          return form.getFormDataByKey('overtime', key);
        })
        .then(vm.displayData)
        .then(() => {
          vm.stopLoadingRequest();
          vm.stopLoadingPage();
          vm.loadingOvertimeData = false;
        });
    });
  },
  data() {
    return {
      loadingApproveReject: false,
      loadingOvertimeData: false,
      userEmployeeData: {},
      overtimeData: {},
      confirmModalOptions: {},
      comments: '',
    };
  },
  computed: {
    approvalNotification() {
      const options = { show: !this.loadingOvertimeData };

      const otherOptions = {};
      switch (this.$get(this.overtimeData, 'statusId', -1)) {
        case this.pendingApproval:
          otherOptions.variant = 'light';
          break;
        case this.approved:
          otherOptions.variant = 'success';
          otherOptions.message = 'Overtime application has been approved.';
          otherOptions.icon = { name: 'check' };
          break;
        case this.rejected:
          otherOptions.variant = 'danger';
          otherOptions.message = 'Overtime application has been rejected.';
          otherOptions.icon = { name: 'times-circle', scale: 1.5 };
          break;
        case this.cancelled:
          otherOptions.variant = 'light ';
          otherOptions.message = 'Overtime application has been cancelled.';
          otherOptions.icon = { name: 'ban' };
          break;
        default:
          otherOptions.variant = 'danger';
          otherOptions.message = 'An error occurred.';
          otherOptions.icon = { name: 'exclamation-triangle' };
          otherOptions.refresh = true;
          break;
      }

      return Object.assign({}, options, otherOptions);
    },
    otherData() {
      return {
        forApproval: true,
        loadingEmployee: this.loadingUserEmployeeData,
        overtimeData: this.overtimeData,
      };
    },
    approvalAction() {
      return this.$get(this.confirmModalOptions, 'action', '');
    },
    approvalNotifText() {
      const { full_name: name } = this.userEmployeeData;
      return `Overtime application for <strong class="text-uppercase">${name}</strong> is pending your approval.`;
    },
    currentStatus() {
      const status = this.$get(this.overtimeData, 'status', null);
      const statusId = this.$get(this.overtimeData, 'statusId', null);

      return !statusId ? null : { status, id: statusId };
    },
    ...mapGetters(['isApprover', 'userData', 'isLoadingRequest']),
    ...mapState(['notification']),
  },
  methods: {
    openConfirm(action) {
      const formattedAction = this.$startCase(action);
      const { full_name: name } = this.userEmployeeData;
      const title = `${formattedAction} Timesheet`;
      const messageHtml = `Are you sure you want to ${action} the overtime for <strong class="text-uppercase">${name}</strong>?`;

      let okBtnVariant = '';
      switch (action) {
        case 'approve':
          okBtnVariant = 'success';
          break;
        case 'reject':
          okBtnVariant = 'danger';
          break;
        default:
          okBtnVariant = 'primary';
          break;
      }

      this.confirmModalOptions = {
        action,
        messageHtml,
        title,
        show: true,
        cancelBtnOptions: {
          variant: 'outline-secondary',
        },
        okBtnOptions: {
          size: 'lg',
          variant: okBtnVariant,
          text: formattedAction,
        },
      };
    },
    displayData(result) {
      if (typeof result === 'object' && result) {
        const {
          success,
          user,
          overtime,
          message,
        } = result;

        if (!success) {
          return this.setNotification({
            show: true,
            refresh: true,
            variant: 'danger',
            message: message || 'An error occurred.',
            icon: { name: 'exclamation-triangle' },
          });
        }

        const overtimeData = {};
        const otKeys = Object.keys(overtime || {});
        otKeys.forEach((k) => {
          if (k === 'totalHours' || k === 'nightDiff') {
            overtimeData[k] = Number(overtime[k] || 0).toFixed(2);
          } else {
            overtimeData[k] = overtime[k];
          }
        });

        this.overtimeData = overtimeData;
        this.userEmployeeData = user;
      }
      return false;
    },
    approveOrReject() {
      const action = this.approvalAction;
      if (!action) {
        // TODO: approval action not set
        return this.setAlert({
          show: 5,
          dismissible: true,
          variant: 'danger',
          message: 'Client error',
        });
      }
      // TODO: actual approve/reject
      const { key } = this.$route.params;
      const { id: approverId } = this.userData;

      this.startLoadingPage();
      this.loadingApproveReject = true;
      // Execute approval (or rejection) of overtime
      return form.updateApprovalStatus('overtime', action, key, approverId, this.comments)
        .then(({ success, message }) => {
          const defaultMessage = success ? `overtime request ${action}ed` : 'An error occurred';

          this.setNotification({
            message: message || defaultMessage,
            show: true,
            dismissible: true,
            refresh: !success,
            variant: success ? 'success' : 'danger',
            icon: {
              name: success ? 'check' : 'exclamation-triangle',
            },
          });

          if (success) {
            // TODO: update current overtime status
            switch (action) {
              case 'approve':
                this.overtimeData = {
                  ...this.overtimeData,
                  statusId: this.approved,
                  status: 'approved',
                };
                break;
              case 'reject':
                this.overtimeData = {
                  ...this.overtimeData,
                  statusId: this.rejected,
                  status: 'rejected',
                };
                break;
              default: break;
            }
          }
        })
        .catch(() => {
          this.setNotification({
            show: true,
            dismissible: true,
            refresh: true,
            variant: 'danger',
            message: 'An error occurred',
            icon: {
              name: 'exclamation-triangle',
            },
          });
        })
        .then(() => {
          this.loadingApproveReject = false;
          this.stopLoadingPage();
        });
    },
    resetAction() {
      this.confirmModalOptions.action = '';
    },
    ...mapActions([
      'setAlert',
      'setNotification',
      'startLoadingRequest',
      'stopLoadingRequest',
      'startLoadingPage',
      'stopLoadingPage',
    ]),
  },
};
</script>
